import { LitElement, html, css } from 'lit';
import { FetchMixin } from '../FetchMixin';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

/**
 * ダッシュボードお知らせ詳細コンポーネント
 */
class DashboardNotificationDetail extends FetchMixin(LitElement) {
  static get styles() {
    return css`
      :host {
        width: 100%;
        height: 100%;
        display: none;
        left: 50%;
        top: 50%;
        transform: translateX(-50%) translateY(-50%);
        position: fixed;
        background: rgba(0, 0, 0, 0.6);
        z-index: 1000;
        user-select: none;
      }

      :host([visible]) {
        display: block;
      }

      .component-content {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .detail-content {
        width: 350px;
        background: white;
        border-radius: 10px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        padding: 20px;
        flex-direction: column;
        gap: 20px;
      }

      .detail-title {
        font-size: 18px;
        font-weight: bold;
        color: #333;
      }

      .detail-body {
        font-size: 14px;
        color: #555;
        white-space: pre-wrap;
        margin-top: 10px;
      }

      .close-button-content {
        display: flex;
        justify-content: center;
        margin-top: 20px;
      }

      .close-button {
        background-color: #e2e6ea;
        border: none;
        border-radius: 4px;
        padding: 10px 20px;
        cursor: pointer;
        font-size: 14px;
        text-align: center;
      }

      .close-button:hover {
        background-color: #d7d7d7;
      }
    `;
  }

  static properties = {
    notificationId: { type: Number },
    notification: { state: true },
    visible: { type: Boolean },
  };

  constructor() {
    super();
    this.notificationId = null;
    this.notification = null;
    this.visible = false;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('notification-selected', this.handleNotificationSelected.bind(this));
  }

  disconnectedCallback() {
    window.removeEventListener('notification-selected', this.handleNotificationSelected.bind(this));
    super.disconnectedCallback();
  }

  /**
   * プロパティ更新時の処理
   * @param {Map} changedProperties 変更されたプロパティ 
   */
  updated(changedProperties) {
    if (changedProperties.has('visible')) {
      this.visible ? this.setAttribute('visible', '') : this.removeAttribute('visible');
    }
  }

  /**
   * お知らせ選択イベント処理
   * @param {CustomEvent} event 
   */
  handleNotificationSelected(event) {
    this.notification = event.detail.notification;
    this.show(); 

    if (!this.notification.isRead) {
      this.markAsRead();
    }
  }

  /**
   * お知らせを既読にする
   */
  async markAsRead() {
    // fetchオプションを設定
    const options = {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ isRead: true }) // 既読状態をtrueに設定
    };

    // .envからエンドポイントを読み込み
    const endpoint = `${process.env.NOTIFICATION_ENDPOINT}/${this.notification.notificationId}/read`;

    // APIを呼び出し
    let response = await this.fetchWithAuth(endpoint, options);

    if (response && response.status === 'notification_read_success') {
      this.dispatchEvent(new CustomEvent('notification-read', {
        detail: { notificationId: this.notification.notificationId },
        bubbles: true,
        composed: true
      }));

      // 再読み込みイベントを発行
      this.dispatchEvent(new CustomEvent('notification-list-reload', {
        bubbles: true,
        composed: true
      }));

    } else {
      throw new Error('お知らせの既読処理に失敗しました');
    }
  }

  show() {
    this.visible = true;
  }

  hide() {
    this.visible = false;
  }

  render() {
    if (!this.notification) {
      return html``;
    }

    const bodyHTML = html`${unsafeHTML(this.notification.body)}`; 

    return html`
      <div class="component-content" @click="${this.hide}">
        <div class="detail-content" @click="${e => e.stopPropagation()}">
          <div class="detail-title">${this.notification.title}</div>
          <div class="detail-body">${bodyHTML}</div>
          <div class="close-button-content">
            <button class="close-button" @click="${this.hide}">閉じる</button>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('dashboard-notification-detail', DashboardNotificationDetail);